import { useState } from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { toast } from "sonner";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../lib/api";
import { TaskCard } from "./TaskCard";
import { MoveReasonDialog } from "./MoveReasonDialog";
import { BOARD_COLUMNS, STATUS_META } from "./taskConstants";

export function TaskBoard({ tasks, onOpenTask, onChanged }) {
  const { token } = useAuth();
  const [pendingMove, setPendingMove] = useState(null); // { taskId, to }
  const [moving, setMoving] = useState(false);

  const getByStatus = (status) => tasks.filter((t) => t.status === status);

  const doMove = async (taskId, to, extra = {}) => {
    try {
      await api.moveTask(token, taskId, { to, ...extra });
      toast.success(`Tarea movida a "${STATUS_META[to]?.label || to}"`);
      onChanged?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "No se pudo mover la tarea — vuelve a su columna.");
      onChanged?.(); // fuerza re-render para que la tarjeta "rebote" a su sitio
    }
  };

  const handleDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination || destination.droppableId === source.droppableId) return;
    const to = destination.droppableId;
    if (to === "in_review" || to === "blocked") {
      setPendingMove({ taskId: draggableId, to });
      return;
    }
    await doMove(draggableId, to);
  };

  const confirmPendingMove = async (text) => {
    if (!pendingMove) return;
    setMoving(true);
    const extra = pendingMove.to === "in_review" ? { summary: text } : { reason: text };
    await doMove(pendingMove.taskId, pendingMove.to, extra);
    setMoving(false);
    setPendingMove(null);
  };

  return (
    <>
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {BOARD_COLUMNS.map((status) => {
            const meta = STATUS_META[status];
            const colTasks = getByStatus(status);
            return (
              <div key={status} className="min-w-[260px] w-[260px] flex-shrink-0 flex flex-col">
                <div className={`flex items-center justify-between px-3 py-2 rounded-t-lg border-x border-t ${meta.border} ${meta.header}`}>
                  <span className="font-semibold text-sm">{meta.label}</span>
                  <span className="text-xs font-mono opacity-70">{colTasks.length}</span>
                </div>
                <Droppable droppableId={status}>
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex-1 min-h-[180px] max-h-[70vh] overflow-y-auto p-2 space-y-2 rounded-b-lg border ${meta.border} transition-colors ${snapshot.isDraggingOver ? "bg-primary/5" : ""}`}
                      data-testid={`task-column-${status}`}
                    >
                      {colTasks.map((task, index) => (
                        <Draggable key={task.id} draggableId={task.id} index={index}>
                          {(dragProvided, dragSnapshot) => (
                            <TaskCard task={task} onClick={() => onOpenTask(task.id)} dragProvided={dragProvided} dragSnapshot={dragSnapshot} />
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                      {colTasks.length === 0 && (
                        <p className="text-xs text-muted-foreground italic text-center py-4">Sin tareas</p>
                      )}
                    </div>
                  )}
                </Droppable>
              </div>
            );
          })}
        </div>
      </DragDropContext>

      <MoveReasonDialog
        open={!!pendingMove}
        kind={pendingMove?.to}
        loading={moving}
        onCancel={() => setPendingMove(null)}
        onConfirm={confirmPendingMove}
      />
    </>
  );
}
